import { createSlice } from '@reduxjs/toolkit';
import { fetchEntityActivities } from './activityThunks';
import { deleteTask } from '../task/taskThunks';
import { deleteNote } from '../note/noteThunks';
import { deleteCall } from '../call/callThunks';
import { deleteEmail } from '../email/emailThunks';
import { deleteMeeting } from '../meeting/meetingThunks';
import { deleteLinkedinPost } from '../linkedinPost/linkedinPostThunks';
import type { ActivityState, ActivitySummaryType } from './activityTypes';

const initialState: ActivityState = {
  byEntity: {},
  status: 'idle',
  error: null,
};

/**
 * Rimuove da TUTTE le liste per-entità l'attività (type + classPK): usato dai
 * reducer di delete così la timeline bulk si aggiorna senza refetch. Il delete
 * di un'attività dalla timeline tocca solo il byId dell'entità (non le
 * Relationship), quindi la firma dei link non cambia: il pruning è l'unico modo
 * per togliere subito l'item dalla proiezione bulk. Match su type E classPK
 * perché i primary key non sono univoci cross-tipo.
 */
function pruneActivity(state: ActivityState, type: ActivitySummaryType, classPK: number): void {
  for (const key of Object.keys(state.byEntity)) {
    state.byEntity[key] = state.byEntity[key].filter(
      (a) => !(a.type === type && a.classPK === classPK),
    );
  }
}

export const activitySlice = createSlice({
  name: 'activities',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchEntityActivities.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchEntityActivities.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.byEntity[action.payload.key] = action.payload.activities;
      })
      .addCase(fetchEntityActivities.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? action.error.message ?? 'Errore';
      });

    // Invalidazione su delete: rimuove l'attività eliminata da ogni lista bulk
    // (payload = id dell'entità cancellata). MESSAGE non è deletabile dalla timeline.
    builder
      .addCase(deleteTask.fulfilled, (state, a) => pruneActivity(state, 'TASK', a.payload))
      .addCase(deleteNote.fulfilled, (state, a) => pruneActivity(state, 'NOTE', a.payload))
      .addCase(deleteCall.fulfilled, (state, a) => pruneActivity(state, 'CALL', a.payload))
      .addCase(deleteEmail.fulfilled, (state, a) => pruneActivity(state, 'EMAIL', a.payload))
      .addCase(deleteMeeting.fulfilled, (state, a) => pruneActivity(state, 'MEETING', a.payload))
      .addCase(deleteLinkedinPost.fulfilled, (state, a) =>
        pruneActivity(state, 'LINKEDIN', a.payload));
  },
});

export const activityReducer = activitySlice.reducer;
